import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { MainLayout } from '../../components/MainLayout';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../../components/ui/tooltip';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, ResponsiveContainer,
  Tooltip as RTooltip, CartesianGrid,
} from 'recharts';
import {
  Download, RotateCcw, Search, FileText, Clock, AlertTriangle, Receipt, Calendar,
} from 'lucide-react';

const fmtC = (n) =>
  n == null ? '$0.00' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d)) return v;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
};

const PfStatusBadge = ({ status }) => {
  const map = {
    review: 'bg-amber-100 text-amber-700',
    approved: 'bg-blue-100 text-blue-700',
    paid: 'bg-emerald-100 text-emerald-700',
  };
  const cls = map[status] || 'bg-slate-100 text-slate-700';
  return <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold ${cls}`}>{status || '—'}</span>;
};

const PayStatusBadge = ({ status }) => {
  if (status === 'paid') return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Paid</Badge>;
  if (status === 'hold') return <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200">Hold</Badge>;
  return <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100">Unpaid</Badge>;
};

const VendorSearchSelect = ({ value, onChange, vendors, dataTestId }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    if (!q) return vendors.slice(0, 100);
    const lc = q.toLowerCase();
    return vendors.filter((v) => v.name?.toLowerCase().includes(lc)).slice(0, 100);
  }, [vendors, q]);
  const selected = vendors.find((v) => v.name === value);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="h-8 w-52 text-xs border rounded px-2 flex items-center justify-between bg-white hover:border-emerald-300"
        data-testid={dataTestId}
      >
        <span className="truncate">{value === 'all' || !value ? 'All vendors' : `${selected?.name || value}`}</span>
        <Search className="h-3 w-3 opacity-50 ml-1 shrink-0" />
      </button>
      {open && (
        <div className="absolute top-full mt-1 left-0 z-30 w-72 bg-white border rounded shadow-lg">
          <div className="p-2 border-b">
            <Input
              autoFocus
              placeholder="Search vendor…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="h-7 text-xs"
            />
          </div>
          <div className="max-h-64 overflow-y-auto">
            <button
              type="button"
              onClick={() => { onChange('all'); setOpen(false); setQ(''); }}
              className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 border-b"
            >
              <span className="font-medium">All vendors</span>
            </button>
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-muted-foreground">No vendors</div>
            ) : (
              filtered.map((v) => (
                <button
                  key={v.name}
                  type="button"
                  onClick={() => { onChange(v.name); setOpen(false); setQ(''); }}
                  className={`w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 flex justify-between ${value === v.name ? 'bg-emerald-50' : ''}`}
                >
                  <span className="truncate">{v.name}</span>
                  <span className="text-muted-foreground ml-2">{v.count} · {fmtC(v.total_fee)}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default function LateFeeReport() {
  const [currentAccount, setCurrentAccount] = useState(() => {
    const s = localStorage.getItem('current_account');
    return s ? JSON.parse(s) : null;
  });
  useEffect(() => {
    const check = () => {
      const s = localStorage.getItem('current_account');
      const acc = s ? JSON.parse(s) : null;
      if (JSON.stringify(acc) !== JSON.stringify(currentAccount)) setCurrentAccount(acc);
    };
    const h = setInterval(check, 800);
    return () => clearInterval(h);
  }, [currentAccount]);
  const accountId = currentAccount?.id;

  const [vendor, setVendor] = useState('all');
  const [acctNumber, setAcctNumber] = useState('');
  const [status, setStatus] = useState('all');
  const [dba, setDba] = useState('all');
  const [page, setPage] = useState(1);

  const [data, setData] = useState(null);
  const [filterOpts, setFilterOpts] = useState({ vendors: [], account_numbers: [] });
  const [charts, setCharts] = useState({ late_arrival_by_month: [], dba_top_vendors: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const params = { account_id: accountId, page, limit: 50 };
      if (vendor && vendor !== 'all') params.vendor = vendor;
      if (acctNumber.trim()) params.account_number = acctNumber.trim();
      if (status && status !== 'all') params.status = status;
      if (dba && dba !== 'all') params.due_before_approval = dba;
      const { data: d } = await api.get('/payments/v2/reports/late-fee', { params });
      setData(d);
    } catch {
      toast.error('Could not load late fee report');
    } finally {
      setLoading(false);
    }
  }, [accountId, vendor, acctNumber, status, dba, page]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!accountId) return;
    api
      .get('/payments/v2/reports/late-fee/filter-options', { params: { account_id: accountId } })
      .then((r) => setFilterOpts(r.data))
      .catch(() => {});
    api
      .get('/payments/v2/reports/late-fee/charts', { params: { account_id: accountId } })
      .then((r) => setCharts(r.data || { late_arrival_by_month: [], dba_top_vendors: [] }))
      .catch(() => {});
  }, [accountId]);

  const rows = data?.rows || [];
  const stats = data?.stats || {};
  const total = data?.total || 0;
  const totalPages = data?.total_pages || 0;

  const downloadCsv = () => {
    if (!accountId) return;
    const qs = new URLSearchParams({ account_id: accountId });
    if (vendor && vendor !== 'all') qs.set('vendor', vendor);
    if (acctNumber.trim()) qs.set('account_number', acctNumber.trim());
    if (status && status !== 'all') qs.set('status', status);
    if (dba && dba !== 'all') qs.set('due_before_approval', dba);
    const token = JSON.parse(localStorage.getItem('auth') || '{}')?.access_token;
    fetch(`${api.defaults.baseURL}/payments/v2/reports/late-fee/export?${qs.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `late_fee_${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error('Could not download'));
  };

  return (
    <MainLayout wide>
      <TooltipProvider delayDuration={200}>
        <div className="space-y-4 p-4 md:p-6" data-testid="late-fee-report-page">
          {/* Header */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-rose-500 to-orange-500 flex items-center justify-center shadow-sm">
                <Clock className="h-4 w-4 text-white" strokeWidth={2.5} />
              </div>
              <div>
                <h1 className="text-xl font-bold" data-testid="late-fee-title">Late Fee Report</h1>
                <p className="text-[11px] text-muted-foreground">
                  Every bill carrying a late payment fee, split by root cause.
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={downloadCsv} disabled={loading} data-testid="late-fee-download">
              <Download className="h-3.5 w-3.5 mr-1" /> Download
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="border rounded-lg p-4 bg-rose-50/40 border-rose-200" data-testid="lfr-stat-total">
              <div className="flex items-center gap-2 mb-1">
                <Receipt className="h-4 w-4 text-rose-600" />
                <span className="text-xs font-semibold uppercase tracking-wide text-rose-700">Total Late Fees</span>
              </div>
              <p className="text-2xl font-bold text-rose-700">{fmtC(stats.total_late_fee)}</p>
              <p className="text-[10px] text-muted-foreground mt-1">
                {stats.bill_count?.toLocaleString() || 0} bills
              </p>
              {/* 4-cell sub-breakdown by payment status + PF status */}
              <div className="mt-2 grid grid-cols-2 gap-1.5 text-[11px]">
                <div className="rounded bg-white border px-2 py-1" data-testid="lfr-sub-paid">
                  <div className="text-muted-foreground">Paid</div>
                  <div className="font-semibold text-emerald-700">{stats.paid_count || 0}</div>
                  <div className="text-[10px] text-muted-foreground">{fmtC(stats.paid_amt)}</div>
                </div>
                <div className="rounded bg-white border px-2 py-1" data-testid="lfr-sub-unpaid">
                  <div className="text-muted-foreground">Unpaid</div>
                  <div className="font-semibold text-rose-700">{stats.unpaid_count || 0}</div>
                  <div className="text-[10px] text-muted-foreground">{fmtC(stats.unpaid_amt)}</div>
                </div>
                <div className="rounded bg-white border px-2 py-1" data-testid="lfr-sub-review">
                  <div className="text-muted-foreground">In Review</div>
                  <div className="font-semibold text-amber-700">{stats.in_review_count || 0}</div>
                  <div className="text-[10px] text-muted-foreground">{fmtC(stats.in_review_amt)}</div>
                </div>
                <div className="rounded bg-white border px-2 py-1" data-testid="lfr-sub-approved">
                  <div className="text-muted-foreground">In Approved</div>
                  <div className="font-semibold text-blue-700">{stats.in_approved_count || 0}</div>
                  <div className="text-[10px] text-muted-foreground">{fmtC(stats.in_approved_amt)}</div>
                </div>
              </div>
            </div>
            <div className="border rounded-lg p-4 bg-amber-50/40 border-amber-200" data-testid="lfr-stat-dba">
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-xs font-semibold uppercase tracking-wide text-amber-700 cursor-help">
                      Due Before Approval
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[320px] text-xs">
                    Bills whose <strong>due date passed before the Payment File was approved</strong>. Late fees in
                    this bucket could be caused by approval delays in BillPortal — review timing and approver SLA.
                    Multiple causes are possible (vendor portal lockouts, missing approvers, etc.), so download the
                    CSV and investigate the offending PFs.
                  </TooltipContent>
                </Tooltip>
              </div>
              <p className="text-2xl font-bold text-amber-700">{fmtC(stats.due_before_approval_amt)}</p>
              <p className="text-[10px] text-muted-foreground mt-1">
                {stats.due_before_approval_cnt || 0} bills affected
              </p>
              {/* Top 5 vendors — horizontal bar by late-fee $ */}
              <div className="mt-3" data-testid="lfr-dba-chart">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                  Top 5 vendors · count + late fee
                </div>
                {charts.dba_top_vendors?.length ? (
                  <ResponsiveContainer width="100%" height={140}>
                    <BarChart
                      data={charts.dba_top_vendors}
                      layout="vertical"
                      margin={{ top: 2, right: 10, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid horizontal={false} strokeDasharray="2 3" stroke="#f5e4c4" />
                      <XAxis type="number" tick={{ fontSize: 9 }} stroke="#a16207" />
                      <YAxis
                        type="category" dataKey="vendor"
                        width={90} tick={{ fontSize: 10 }} stroke="#a16207"
                        tickFormatter={(v) => (v && v.length > 14 ? v.slice(0, 13) + '…' : v)}
                      />
                      <RTooltip
                        formatter={(value, name) => name === 'amount' ? fmtC(value) : `${value} bills`}
                        labelStyle={{ fontSize: 11 }} contentStyle={{ fontSize: 11 }}
                      />
                      <Bar dataKey="amount" fill="#d97706" name="amount" radius={[0, 2, 2, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-[10px] text-muted-foreground italic py-4 text-center">
                    No vendors in this bucket yet.
                  </div>
                )}
              </div>
            </div>
            <div className="border rounded-lg p-4 bg-slate-50/60 border-slate-200" data-testid="lfr-stat-late-arrival">
              <div className="flex items-center gap-2 mb-1">
                <Calendar className="h-4 w-4 text-slate-600" />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-xs font-semibold uppercase tracking-wide text-slate-700 cursor-help">
                      Late Arrival at BillPortal
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-[320px] text-xs">
                    Bills whose due date was already in the past when BillPortal first received them
                    (<em>bill received date &gt; due date</em>). These late fees are NOT caused by us — they were
                    overdue before ingestion.
                  </TooltipContent>
                </Tooltip>
              </div>
              <p className="text-2xl font-bold text-slate-700">{fmtC(stats.due_before_arrival_amt)}</p>
              <p className="text-[10px] text-muted-foreground mt-1">
                {stats.due_before_arrival_cnt || 0} bills affected
              </p>
              {/* Last 12 months — bill count by month */}
              <div className="mt-3" data-testid="lfr-late-arrival-chart">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">
                  Last 12 months · bill count
                </div>
                {charts.late_arrival_by_month?.length ? (
                  <ResponsiveContainer width="100%" height={140}>
                    <LineChart
                      data={charts.late_arrival_by_month}
                      margin={{ top: 2, right: 8, left: -16, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="2 3" stroke="#e5e7eb" />
                      <XAxis dataKey="label" tick={{ fontSize: 9 }} stroke="#64748b" interval="preserveStartEnd" />
                      <YAxis tick={{ fontSize: 9 }} stroke="#64748b" allowDecimals={false} />
                      <RTooltip
                        formatter={(value, name) => name === 'amount' ? fmtC(value) : `${value} bills`}
                        labelStyle={{ fontSize: 11 }} contentStyle={{ fontSize: 11 }}
                      />
                      <Line
                        type="monotone" dataKey="count" stroke="#475569"
                        strokeWidth={2} dot={{ r: 2.5, fill: '#475569' }} name="count"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="text-[10px] text-muted-foreground italic py-4 text-center">
                    No late-arrival bills in the last 12 months.
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="border rounded-lg p-3 bg-card flex flex-wrap gap-2 items-center" data-testid="lfr-filters">
            <VendorSearchSelect
              value={vendor}
              onChange={(v) => { setVendor(v); setPage(1); }}
              vendors={filterOpts.vendors || []}
              dataTestId="lfr-filter-vendor"
            />
            <Select value={dba} onValueChange={(v) => { setDba(v); setPage(1); }}>
              <SelectTrigger className="h-8 w-52 text-xs" data-testid="lfr-filter-dba"><SelectValue placeholder="Due before approval" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any approval timing</SelectItem>
                <SelectItem value="yes">Yes — delayed approval</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
            <Select value={status} onValueChange={(v) => { setStatus(v); setPage(1); }}>
              <SelectTrigger className="h-8 w-36 text-xs" data-testid="lfr-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any status</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="unpaid">Unpaid</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
              <Input
                value={acctNumber} onChange={(e) => { setAcctNumber(e.target.value); setPage(1); }}
                placeholder="Account #" className="h-8 w-40 text-xs pl-6"
                data-testid="lfr-filter-acctnum"
              />
            </div>
            <Button variant="ghost" size="sm" onClick={() => {
              setVendor('all'); setAcctNumber(''); setStatus('all'); setDba('all'); setPage(1);
            }} className="h-8 text-xs" data-testid="lfr-filter-reset">
              <RotateCcw className="h-3 w-3 mr-1" /> Reset
            </Button>
            <div className="ml-auto text-[11px] text-muted-foreground">
              {loading ? 'Loading…' : `${total.toLocaleString()} bills`}
            </div>
          </div>

          {/* Table */}
          <div className="rounded-lg border bg-card overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40">
                  <TableHead className="text-[10px] uppercase">Property / Address</TableHead>
                  <TableHead className="text-[10px] uppercase">Account #</TableHead>
                  <TableHead className="text-[10px] uppercase w-32">Vendor</TableHead>
                  <TableHead className="text-[10px] uppercase w-24">Issue Date</TableHead>
                  <TableHead className="text-[10px] uppercase">Commodity</TableHead>
                  <TableHead className="text-[10px] uppercase">Status</TableHead>
                  <TableHead className="text-[10px] uppercase">PF Batch</TableHead>
                  <TableHead className="text-[10px] uppercase text-right">Payment Amt</TableHead>
                  <TableHead className="text-[10px] uppercase">Due Date</TableHead>
                  <TableHead className="text-[10px] uppercase text-right">Late Fee</TableHead>
                  <TableHead className="text-[10px] uppercase">Approval Time</TableHead>
                  <TableHead className="text-[10px] uppercase">Due ▸ Approval</TableHead>
                  <TableHead className="text-[10px] uppercase w-8"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && rows.length === 0 && (
                  Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={`s${i}`}>
                      <TableCell colSpan={13}><Skeleton className="h-6 w-full" /></TableCell>
                    </TableRow>
                  ))
                )}
                {!loading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={13} className="text-center text-xs text-muted-foreground py-8">
                      No bills with late fees in this scope.
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((r) => (
                  <TableRow key={`${r.bill_id}_${r.payment_file_id}`} data-testid={`lfr-row-${r.bill_id}`}>
                    <TableCell className="text-xs">
                      <div className="font-medium">{r.property_name || '—'}</div>
                      <div className="text-[10px] text-muted-foreground">{r.service_address || r.property_address || '—'}</div>
                    </TableCell>
                    <TableCell className="text-xs">{r.account_number || '—'}</TableCell>
                    <TableCell className="text-xs">
                      <div className="truncate max-w-[8rem]" title={r.supplier_name || ''}>
                        {r.supplier_name || '—'}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">{fmtDate(r.invoice_date)}</TableCell>
                    <TableCell className="text-xs">
                      {(r.commodity || r.utility_type || '—').toString().split(/\s*,\s*/).slice(0, 1).map((c, i) => (
                        <span key={i} className="inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[10px]">{c}</span>
                      ))}
                      {(r.commodity || '').toString().split(/\s*,\s*/).length > 1 && (
                        <span className="ml-1 text-[10px] text-muted-foreground">multi</span>
                      )}
                    </TableCell>
                    <TableCell><PayStatusBadge status={r.payment_status} /></TableCell>
                    <TableCell className="text-xs">
                      <div className="font-medium">{r.pf_batch_name || '—'}</div>
                      <PfStatusBadge status={r.pf_status} />
                    </TableCell>
                    <TableCell className="text-xs text-right">{fmtC(r.payment_amount)}</TableCell>
                    <TableCell className="text-xs">{fmtDate(r.due_date)}</TableCell>
                    <TableCell className="text-xs text-right font-semibold text-rose-700">{fmtC(r.late_fee)}</TableCell>
                    <TableCell className="text-xs">
                      {r.approval_days != null ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex items-center gap-1 cursor-help">
                              <Clock className="h-3 w-3" /> {r.approval_days} d
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>
                            Days taken to approve this PF after generation
                            ({fmtDate(r.pf_generated_at)} → {fmtDate(r.pf_approved_at)})
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">
                      {r.due_before_approval ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 cursor-help">Yes</Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            Due date {fmtDate(r.due_date)} preceded approval
                            ({fmtDate(r.pf_approved_at)}). Approval delay may have contributed to this late fee.
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <span className="text-muted-foreground">No</span>
                      )}
                      {r.due_before_arrival && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge className="ml-1 bg-slate-200 text-slate-700 hover:bg-slate-200 cursor-help">late arrival</Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            Bill arrived at BillPortal on {fmtDate(r.bill_received_date)} — already past due.
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.bill_pdf_url ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <a href={r.bill_pdf_url} target="_blank" rel="noopener noreferrer"
                               className="rounded p-1 hover:bg-slate-100 text-slate-500 inline-block"
                               data-testid={`lfr-pdf-${r.bill_id}`}>
                              <FileText className="h-3.5 w-3.5" />
                            </a>
                          </TooltipTrigger>
                          <TooltipContent>Open bill PDF</TooltipContent>
                        </Tooltip>
                      ) : (
                        <span className="inline-block w-4" />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex justify-between items-center text-xs">
              <span className="text-muted-foreground">Page {page} of {totalPages}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </div>
      </TooltipProvider>
    </MainLayout>
  );
}
